import { useMemo } from 'react'

import { keepPreviousData } from '@tanstack/react-query'
import isNil from 'lodash.isnil'
import omit from 'lodash.omit'
import {
  AddAddressFieldToRecordMutation,
  AddAddressFieldToRecordMutationVariables,
  AddMissingTemplateFieldsMutation,
  AddMissingTemplateFieldsMutationVariables,
  CancelOrCloseRecordMutation,
  CancelOrCloseRecordMutationVariables,
  ConnectRecordsMutation,
  ConnectRecordsMutationVariables,
  ConnectRecordsNonHierarchicallyMutation,
  ConnectRecordsNonHierarchicallyMutationVariables,
  CreateRecordCasesMutation,
  CreateRecordCasesMutationVariables,
  CreateRecordDraftMutation,
  CreateRecordDraftMutationVariables,
  CreateRecordMutation,
  CreateRecordMutationVariables,
  DeleteRecordMutation,
  DeleteRecordMutationVariables,
  DisconnectNonhierarchicalRecordConnectionsMutation,
  DisconnectNonhierarchicalRecordConnectionsMutationVariables,
  DisconnectRecordMutation,
  DisconnectRecordMutationVariables,
  DisconnectRenewalRecordFromParentRecordMutation,
  DisconnectRenewalRecordFromParentRecordMutationVariables,
  GenerateRecordCaseReportMutation,
  GenerateRecordCaseReportMutationVariables,
  GenerateRecordMailerMutation,
  GenerateRecordMailerMutationVariables,
  GenerateViolationsCsvMutation,
  GenerateViolationsCsvMutationVariables,
  GetImageFilesOnRecordAndAncestorsQuery,
  GetImageFilesOnRecordAndAncestorsQueryVariables,
  GetRecordFilesListQuery,
  GetRecordFilesListQueryVariables,
  GetRecordIdQuery,
  GetRecordIdQueryVariables,
  GetRecordTypeIdForRecord,
  GetRecordTypeIdForRecordVariables,
  MarkRecordAsRenewalMutation,
  MarkRecordAsRenewalMutationVariables,
  MyApplicantRenewableRecordsQuery,
  MyApplicantRenewableRecordsQueryVariables,
  ReassignRecordMutation,
  ReassignRecordMutationVariables,
  RecordApplicationFieldsQuery,
  RecordApplicationFieldsQueryVariables,
  RecordQuery,
  RecordQueryVariables,
  RecordSearchQuery,
  RecordSearchQueryVariables,
  RenewRecordInPlaceMutation,
  RenewRecordInPlaceMutationVariables,
  RequestRecordsMutation,
  RequestRecordsMutationVariables,
  ResolveRecordCaseMutation,
  ResolveRecordCaseMutationVariables,
  SubmitRecordDraftMutation,
  SubmitRecordDraftMutationVariables,
  UpdateIsApplicantAnonymizedMutationVariables,
  UpdateIsApplicantAnonymizedMutation,
  UpdateRecordDraftMutation,
  UpdateRecordDraftMutationVariables,
  UpdateRecordExpirationDatesMutation,
  UpdateRecordExpirationDatesMutationVariables,
  UpdateRecordRecordTemplateMutation,
  UpdateRecordRecordTemplateMutationVariables,
  UpdateRecordStatusMutation,
  UpdateRecordStatusMutationVariables,
  UpdateRecordTagsMutation,
  UpdateRecordTagsMutationVariables,
  UpdateRecordViolationTypeMutation,
  UpdateRecordViolationTypeMutationVariables,
  UploadFilesToRecord,
  UploadFilesToRecordVariables,
  UpdateRecordApplicantUserAndCompanyMutation,
  UpdateRecordApplicantUserAndCompanyMutationVariables,
  DeleteRecordsMutation,
  DeleteRecordsMutationVariables,
  ActivateRecordMutation,
  ActivateRecordMutationVariables,
  GenerateIndividualRecordReportMutation,
  GenerateIndividualRecordReportMutationVariables,
  GetRecordUpdatedRecordTemplateHistoriesQuery,
  GetRecordUpdatedRecordTemplateHistoriesQueryVariables,
  GetAlertsForRecordQuery,
  GetAlertsForRecordQueryVariables,
  GenerateNearbyPropertiesReportMutation,
  GenerateNearbyPropertiesReportMutationVariables,
  GetRecordIssuedIdentifierQuery,
  GetRecordIssuedIdentifierQueryVariables,
  UpdateRecordIssuedIdentifierMutation,
  UpdateRecordIssuedIdentifierMutationVariables,
  InitiateRenewalInPlaceMutation,
  InitiateRenewalInPlaceMutationVariables,
  InitiateRenewalNewRecordMutation,
  InitiateRenewalNewRecordMutationVariables,
} from 'types/graphql'

import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import {
  QueryKey,
  UseQueryOptions,
  queryKeyFactories,
  useMutation,
  useQuery,
} from 'src/utils/queryClient'
import { useRecordApplicationFields } from 'src/utils/records'

const GetRecord = gql`
  query RecordQuery($recordUuid: String!) {
    record: recordByUuid(uuid: $recordUuid) {
      ...RecordFragment
    }
  }
`

// Powers the applicant dashboard: loads all records the current applicant
// owns or has access to that are in the renewal lifecycle (Active,
// EligibleToRenew, GracePeriod, LatePeriod, or RenewalInProgress). The
// dashboard renders a login-alert banner per warning-status record and a
// "My Records" table from the same dataset.
const GetMyApplicantRenewableRecords = gql`
  query MyApplicantRenewableRecordsQuery {
    records: myApplicantRenewableRecords {
      ...RecordFragmentApplicantDashboard
    }
  }
`

export const useMyApplicantRenewableRecordsQuery = (
  options?: UseQueryOptions<
    MyApplicantRenewableRecordsQuery,
    MyApplicantRenewableRecordsQueryVariables
  >
) => {
  const result = useQuery<
    MyApplicantRenewableRecordsQuery,
    MyApplicantRenewableRecordsQueryVariables
  >({
    queryKey: [QueryKey.MyApplicantRenewableRecords],
    queryDocument: GetMyApplicantRenewableRecords,
    variables: {},
    ...options,
  })
  // Fall back to an empty list so consumers can render without guarding
  // against undefined while the query is in flight.
  const records = useMemo(() => result.data?.records ?? [], [result.data?.records])
  return {
    ...omit(result, 'data'),
    records,
  }
}
export const useRecordQuery = (
  recordUuid: string,
  options?: UseQueryOptions<RecordQuery, RecordQueryVariables>
) => {
  const result = useQuery<RecordQuery, RecordQueryVariables>({
    queryKey: queryKeyFactories.recordPage(recordUuid),
    queryDocument: GetRecord,
    variables: { recordUuid },
    retry: false,
    refetchOnWindowFocus: true,
    ...options,
  })
  const record = useMemo(() => result.data?.record, [result.data?.record])
  return {
    ...omit(result, 'data'),
    record,
  }
}

const QueryRecordApplicationFields = gql`
  query RecordApplicationFieldsQuery($recordUuid: String!) {
    recordByUuid(uuid: $recordUuid) {
      id
      identifier
      isDraft
      applicantUser {
        ...UserDataFragment
      }
      applicantCompany {
        ...CompanyFragment
      }
      currentFormFieldGroupVersion {
        id
        fieldGroups {
          ...FieldGroupValueDetailedFragment
        }
      }
      filesFieldGroup {
        ...FieldGroupFragment
      }
      formFileFieldGroup {
        ...FieldGroupFragment
      }
      latestAutoCheckExecution {
        id
      }
      recordTemplate {
        id
        uuid
      }
    }
  }
`

export const useRecordApplicationFieldsQuery = (args: {
  recordUuid: string
  enabled?: boolean
  excludeStaffOnlyGroups?: boolean
}) => {
  const queryResult = useQuery<RecordApplicationFieldsQuery, RecordApplicationFieldsQueryVariables>(
    {
      queryKey: [QueryKey.RecordApplicationFields, args.recordUuid],
      queryDocument: QueryRecordApplicationFields,
      variables: {
        recordUuid: args.recordUuid,
      },
      enabled: !isNil(args.enabled) ? args.enabled : true,
      gcTime: 0,
    }
  )

  const record = queryResult.data?.recordByUuid
  const applicantUser = record?.applicantUser
  const applicantCompany = record?.applicantCompany
  const fields = useRecordApplicationFields({
    formFieldGroups: record?.currentFormFieldGroupVersion?.fieldGroups || [],
    formFileFieldGroup: record?.formFileFieldGroup,
    filesFieldGroup: record?.filesFieldGroup,
    excludeStaffOnlyGroups: args.excludeStaffOnlyGroups,
  })

  return {
    ...queryResult,
    record,
    applicantCompany,
    applicantUser,
    fields,
  }
}

const GetImageFilesOnRecordAndAncestors = gql`
  query GetImageFilesOnRecordAndAncestorsQuery($recordId: Int!) {
    imageFilesOnRecordAndAncestors(recordId: $recordId) {
      ...FileFragment
      thumbnailUrl {
        signedUrl
      }
    }
  }
`
export const useImageFilesOnRecordAndAncestorsQuery = (recordId: number) => {
  return useQuery<
    GetImageFilesOnRecordAndAncestorsQuery,
    GetImageFilesOnRecordAndAncestorsQueryVariables
  >({
    queryKey: [QueryKey.ImageFilesOnRecordAndAncestors, recordId],
    queryDocument: GetImageFilesOnRecordAndAncestors,
    variables: {
      recordId,
    },
  })
}

const UpdateRecordRecordTemplate = gql`
  mutation UpdateRecordRecordTemplateMutation(
    $recordId: Int!
    $recordTemplateId: Int!
    $doNotModifyIssuedIdentifier: Boolean
  ) {
    updateRecordRecordTemplate(
      id: $recordId
      recordTemplateId: $recordTemplateId
      doNotModifyIssuedIdentifier: $doNotModifyIssuedIdentifier
    ) {
      id
    }
  }
`

export const useUpdateRecordRecordTemplateMutation = () => {
  return useMutation<
    UpdateRecordRecordTemplateMutation,
    UpdateRecordRecordTemplateMutationVariables
  >({
    mutationDocument: UpdateRecordRecordTemplate,
  })
}

const UpdateApplicantUserAndCompany = gql`
  mutation UpdateRecordApplicantUserAndCompanyMutation(
    $id: Int!
    $input: UpdateRecordApplicantUserAndCompanyInput!
  ) {
    updateRecordApplicantUserAndCompany(id: $id, input: $input) {
      id
    }
  }
`
export const useUpdateRecordApplicantUserAndCompanyMutation = () => {
  return useMutation<
    UpdateRecordApplicantUserAndCompanyMutation,
    UpdateRecordApplicantUserAndCompanyMutationVariables
  >({
    mutationDocument: UpdateApplicantUserAndCompany,
  })
}

const UpdateIsApplicantAnonymized = gql`
  mutation UpdateIsApplicantAnonymizedMutation($recordId: Int!, $isApplicantAnonymized: Boolean!) {
    updateIsApplicantAnonymized(
      recordId: $recordId
      isApplicantAnonymized: $isApplicantAnonymized
    ) {
      id
    }
  }
`
export const useUpdateIsApplicantAnonymizedMutation = () => {
  return useMutation<
    UpdateIsApplicantAnonymizedMutation,
    UpdateIsApplicantAnonymizedMutationVariables
  >({
    mutationDocument: UpdateIsApplicantAnonymized,
  })
}

const CreateRecord = gql`
  mutation CreateRecordMutation($input: CreateRecordInput!) {
    createRecord(input: $input) {
      ...RecordOptionFragment
    }
  }
`
export const useCreateRecordMutation = () => {
  return useMutation<CreateRecordMutation, CreateRecordMutationVariables>({
    mutationDocument: CreateRecord,
  })
}

const CreateRecordCases = gql`
  mutation CreateRecordCasesMutation($input: CreateRecordCasesInput!) {
    createRecordCases(input: $input) {
      ...RecordOptionFragment
    }
  }
`
export const useCreateRecordCasesMutation = () => {
  return useMutation<CreateRecordCasesMutation, CreateRecordCasesMutationVariables>({
    mutationDocument: CreateRecordCases,
  })
}

const SubmitRecordDraft = gql`
  mutation SubmitRecordDraftMutation($id: Int!, $input: CreateRecordInput!) {
    submitRecordDraft(id: $id, input: $input) {
      ...RecordOptionFragment
    }
  }
`
export const useSubmitRecordDraftMutation = () => {
  return useMutation<SubmitRecordDraftMutation, SubmitRecordDraftMutationVariables>({
    mutationDocument: SubmitRecordDraft,
  })
}

const CreateRecordDraft = gql`
  mutation CreateRecordDraftMutation($input: CreateRecordInput!) {
    createRecordDraft(input: $input) {
      ...RecordOptionFragment
    }
  }
`
export const useCreateRecordDraftMutation = () => {
  return useMutation<CreateRecordDraftMutation, CreateRecordDraftMutationVariables>({
    mutationDocument: CreateRecordDraft,
  })
}

const UpdateRecordDraft = gql`
  mutation UpdateRecordDraftMutation($id: Int!, $input: CreateRecordInput!) {
    updateRecordDraft(id: $id, input: $input) {
      ...RecordOptionFragment
    }
  }
`
export const useUpdateRecordDraftMutation = () => {
  return useMutation<UpdateRecordDraftMutation, UpdateRecordDraftMutationVariables>({
    mutationDocument: UpdateRecordDraft,
  })
}

const CancelOrClose = gql`
  mutation CancelOrCloseRecordMutation($id: Int!, $input: CancelOrCloseRecordInput!) {
    cancelOrCloseRecord(id: $id, input: $input) {
      id
    }
  }
`
export const useCancelOrCloseRecordMutation = () => {
  return useMutation<CancelOrCloseRecordMutation, CancelOrCloseRecordMutationVariables>({
    mutationDocument: CancelOrClose,
  })
}

const ActivateRecord = gql`
  mutation ActivateRecordMutation($input: ActivateRecordInput!) {
    activateRecord(input: $input) {
      id
    }
  }
`
export const useActivateRecordMutation = () => {
  return useMutation<ActivateRecordMutation, ActivateRecordMutationVariables>({
    mutationDocument: ActivateRecord,
  })
}

const Update = gql`
  mutation UpdateRecordExpirationDatesMutation(
    $ids: [Int!]!
    $expirationDate: DateTime
    $expirationRecordTypeStatusId: Int
  ) {
    updateRecordExpirationDates(
      ids: $ids
      expirationDate: $expirationDate
      expirationRecordTypeStatusId: $expirationRecordTypeStatusId
    )
  }
`
export const useUpdateRecordExpirationDatesMutation = () => {
  return useMutation<
    UpdateRecordExpirationDatesMutation,
    UpdateRecordExpirationDatesMutationVariables
  >({
    mutationDocument: Update,
  })
}

const UpdateRecordTags = gql`
  mutation UpdateRecordTagsMutation($input: UpdateRecordTagsInput!) {
    updateRecordTags(input: $input) {
      id
    }
  }
`
export const useUpdateRecordTagsMutation = () => {
  return useMutation<UpdateRecordTagsMutation, UpdateRecordTagsMutationVariables>({
    mutationDocument: UpdateRecordTags,
  })
}

const UpdateRecordViolationType = gql`
  mutation UpdateRecordViolationTypeMutation($id: Int!, $violationTypeId: Int!) {
    updateRecordViolationType(id: $id, violationTypeId: $violationTypeId) {
      id
    }
  }
`
export const useUpdateRecordViolationTypeMutation = () => {
  return useMutation<UpdateRecordViolationTypeMutation, UpdateRecordViolationTypeMutationVariables>(
    {
      mutationDocument: UpdateRecordViolationType,
    }
  )
}

const UploadFiles = gql`
  mutation UploadFilesToRecord($input: UploadFilesToRecordInput!) {
    uploadFilesToRecord(input: $input) {
      id
    }
  }
`
export const useUploadFilesToRecord = () => {
  return useMutation<UploadFilesToRecord, UploadFilesToRecordVariables>({
    mutationDocument: UploadFiles,
  })
}

const CreateMailer = gql`
  mutation GenerateRecordMailerMutation($input: CreateRecordMailerInput!) {
    createRecordMailer(input: $input) {
      id
    }
  }
`
export const useGenerateRecordMailerMutation = () => {
  return useMutation<GenerateRecordMailerMutation, GenerateRecordMailerMutationVariables>({
    mutationDocument: CreateMailer,
  })
}

const AddAddressFieldToRecord = gql`
  mutation AddAddressFieldToRecordMutation($recordId: Int!, $input: AddFieldToFieldGroupInput!) {
    addAddressFieldToRecord(recordId: $recordId, input: $input) {
      id
    }
  }
`

export const useAddAddressFieldToRecordMutation = () => {
  return useMutation<AddAddressFieldToRecordMutation, AddAddressFieldToRecordMutationVariables>({
    mutationDocument: AddAddressFieldToRecord,
  })
}

const UpdateStatus = gql`
  mutation UpdateRecordStatusMutation($id: Int!, $statusId: Int!) {
    updateRecordStatus(id: $id, statusId: $statusId) {
      id
    }
  }
`
export const useUpdateRecordStatusMutation = () => {
  return useMutation<UpdateRecordStatusMutation, UpdateRecordStatusMutationVariables>({
    mutationDocument: UpdateStatus,
  })
}

const Resolve = gql`
  mutation ResolveRecordCaseMutation($id: Int!, $input: ResolveRecordCaseInput!) {
    resolveRecordCase(id: $id, input: $input) {
      id
    }
  }
`
export const useResolveRecordCaseMutation = () => {
  return useMutation<ResolveRecordCaseMutation, ResolveRecordCaseMutationVariables>({
    mutationDocument: Resolve,
  })
}

const Delete = gql`
  mutation DeleteRecordMutation($id: Int!) {
    deleteRecord(id: $id) {
      id
    }
  }
`
export const useDeleteRecordMutation = () => {
  return useMutation<DeleteRecordMutation, DeleteRecordMutationVariables>({
    mutationDocument: Delete,
  })
}

const DeleteRecords = gql`
  mutation DeleteRecordsMutation($ids: [Int!]!) {
    deleteRecords(ids: $ids)
  }
`
export const useDeleteRecordsMutation = () => {
  return useMutation<DeleteRecordsMutation, DeleteRecordsMutationVariables>({
    mutationDocument: DeleteRecords,
  })
}

const Reassign = gql`
  mutation ReassignRecordMutation($id: Int!, $userId: Int!) {
    reassignRecord(id: $id, userId: $userId) {
      id
    }
  }
`
export const useReassignRecordMutation = () => {
  return useMutation<ReassignRecordMutation, ReassignRecordMutationVariables>({
    mutationDocument: Reassign,
  })
}

const GenerateViolationsCsv = gql`
  mutation GenerateViolationsCsvMutation($input: SearchRecordsInput!, $fileName: String!) {
    generateRecordsCsv(input: $input, fileName: $fileName) {
      csvUrl
    }
  }
`
export const useGenerateViolationsCsvMutation = () => {
  return useMutation<GenerateViolationsCsvMutation, GenerateViolationsCsvMutationVariables>({
    mutationDocument: GenerateViolationsCsv,
  })
}

const RequestRecords = gql`
  mutation RequestRecordsMutation($input: RequestRecordsInput!) {
    requestRecords(input: $input) {
      id
    }
  }
`
export const useRequestRecordsMutation = () => {
  return useMutation<RequestRecordsMutation, RequestRecordsMutationVariables>({
    mutationDocument: RequestRecords,
  })
}

const SearchRecords = gql`
  query RecordSearchQuery($input: SearchRecordsInput!) {
    searchRecords(input: $input) {
      count
      data {
        ...RecordOptionFragment
      }
    }
  }
`
type SearchArgs = {
  recordTemplateIds?: number[]
  recordTypeId?: number
  searchQuery: string
  isTest?: boolean
}
export const useSearchRecordsQuery = (input: SearchArgs) => {
  const organizationId = useMyOrgId()
  const result = useQuery<RecordSearchQuery, RecordSearchQueryVariables>({
    queryKey: [QueryKey.RecordsSearch, input, organizationId],
    queryDocument: SearchRecords,
    variables: {
      input: {
        filters: {
          ...(input.recordTemplateIds
            ? {
                recordTemplateIds: {
                  values: input.recordTemplateIds,
                },
              }
            : {}),
          ...(input.isTest ? { isTest: { value: input.isTest } } : {}),
        },
        ...(input.recordTypeId
          ? {
              recordTypeId: input.recordTypeId,
            }
          : {}),
        organizationId,
        pageSize: 50,
        query: input.searchQuery,
        type: 'All',
      },
    },
    enabled: !!input.searchQuery && !!organizationId,
    placeholderData: keepPreviousData,
    enableCancellation: true,
  })
  const records = useMemo(
    () => result.data?.searchRecords?.data ?? [],
    [result.data?.searchRecords]
  )
  return {
    ...omit(result, 'data'),
    records,
  }
}

const SearchRecordTags = gql`
  query SearchRecordTagsQuery($query: String!, $recordTypeId: Int, $organizationId: Int) {
    searchRecordTags(query: $query, recordTypeId: $recordTypeId, organizationId: $organizationId)
  }
`
type SearchRecordTagsArgs = {
  query: string
  recordTypeId?: number
}
export const useSearchRecordTagsQuery = (input: SearchRecordTagsArgs) => {
  const organizationId = useMyOrgId()
  const result = useQuery<
    { searchRecordTags: string[] },
    { query: string; recordTypeId?: number; organizationId?: number }
  >({
    queryKey: [QueryKey.RecordTagsSearch, input, organizationId],
    queryDocument: SearchRecordTags,
    variables: {
      query: input.query,
      organizationId: organizationId ?? undefined,
      ...(input.recordTypeId != null ? { recordTypeId: input.recordTypeId } : {}),
    },
    enabled: !!input.query.trim() && !!organizationId,
    placeholderData: keepPreviousData,
  })

  return {
    tags: useMemo(() => result.data?.searchRecordTags ?? [], [result.data?.searchRecordTags]),
    isFetching: result.isFetching,
  }
}

const ConnectRecords = gql`
  mutation ConnectRecordsMutation($parentRecordId: Int!, $childRecordIds: [Int!]!) {
    connectRecords(parentRecordId: $parentRecordId, childRecordIds: $childRecordIds) {
      id
    }
  }
`
export const useConnectRecordsMutation = () => {
  return useMutation<ConnectRecordsMutation, ConnectRecordsMutationVariables>({
    mutationDocument: ConnectRecords,
  })
}

const ConnectRecordsNonHierarchically = gql`
  mutation ConnectRecordsNonHierarchicallyMutation($recordId: Int!, $otherRecordIds: [Int!]!) {
    connectRecordsNonHierarchically(recordId: $recordId, otherRecordIds: $otherRecordIds) {
      id
    }
  }
`
export const useConnectRecordsNonHierarchicallyMutation = () => {
  return useMutation<
    ConnectRecordsNonHierarchicallyMutation,
    ConnectRecordsNonHierarchicallyMutationVariables
  >({
    mutationDocument: ConnectRecordsNonHierarchically,
  })
}

const DisconnectChildFromParentRecord = gql`
  mutation DisconnectRecordMutation($childRecordId: Int!) {
    disconnectChildFromParentRecord(childRecordId: $childRecordId) {
      id
    }
  }
`
export const useDisconnectChildFromParentRecordMutation = () => {
  return useMutation<DisconnectRecordMutation, DisconnectRecordMutationVariables>({
    mutationDocument: DisconnectChildFromParentRecord,
  })
}

const DisconnectNonhierarchicalRecordConnections = gql`
  mutation DisconnectNonhierarchicalRecordConnectionsMutation(
    $recordId: Int!
    $otherRecordIds: [Int!]!
  ) {
    disconnectRecordsNonHierarchically(recordId: $recordId, otherRecordIds: $otherRecordIds) {
      id
    }
  }
`
export const useDisconnectNonhierarchicalRecordConnectionsMutation = () => {
  return useMutation<
    DisconnectNonhierarchicalRecordConnectionsMutation,
    DisconnectNonhierarchicalRecordConnectionsMutationVariables
  >({
    mutationDocument: DisconnectNonhierarchicalRecordConnections,
  })
}

const MarkRecordAsRenewal = gql`
  mutation MarkRecordAsRenewalMutation($input: MarkRecordAsRenewalOfOtherRecordInput!) {
    markRecordAsRenewalOfOtherRecord(input: $input)
  }
`

export const useMarkRecordAsRenewalMutation = () => {
  return useMutation<MarkRecordAsRenewalMutation, MarkRecordAsRenewalMutationVariables>({
    mutationDocument: MarkRecordAsRenewal,
  })
}

const DisconnectRenewalRecordFromParentRecord = gql`
  mutation DisconnectRenewalRecordFromParentRecordMutation($renewalRecordId: Int!) {
    disconnectRenewalRecordFromParentRecord(renewalRecordId: $renewalRecordId)
  }
`

export const useDisconnectRenewalRecordFromParentRecordMutation = () => {
  return useMutation<
    DisconnectRenewalRecordFromParentRecordMutation,
    DisconnectRenewalRecordFromParentRecordMutationVariables
  >({
    mutationDocument: DisconnectRenewalRecordFromParentRecord,
  })
}
const GenerateCaseReport = gql`
  mutation GenerateRecordCaseReportMutation($id: Int!) {
    generateRecordCaseReport(id: $id) {
      ...GeneratedFileFragment
    }
  }
`

export const useGenerateRecordCaseReportMutation = () => {
  return useMutation<GenerateRecordCaseReportMutation, GenerateRecordCaseReportMutationVariables>({
    mutationDocument: GenerateCaseReport,
  })
}

const GenerateIndividualRecordReport = gql`
  mutation GenerateIndividualRecordReportMutation($input: GenerateIndividualRecordReportParams!) {
    generateIndividualRecordReport(input: $input) {
      ...GeneratedFileFragment
    }
  }
`

export const useGenerateIndividualRecordReportMutation = () => {
  return useMutation<
    GenerateIndividualRecordReportMutation,
    GenerateIndividualRecordReportMutationVariables
  >({
    mutationDocument: GenerateIndividualRecordReport,
  })
}

const AddMissingTemplateFields = gql`
  mutation AddMissingTemplateFieldsMutation($recordId: Int!) {
    addMissingTemplateFieldsToCurrentFormFieldGroupVersion(recordId: $recordId) {
      id
    }
  }
`
export const useAddMissingTemplateFieldsToCurrentFormFieldGroupVersionMutation = () => {
  return useMutation<AddMissingTemplateFieldsMutation, AddMissingTemplateFieldsMutationVariables>({
    mutationDocument: AddMissingTemplateFields,
  })
}

const GetRecordFilesList = gql`
  query GetRecordFilesListQuery($recordId: Int!) {
    recordById(id: $recordId) {
      formFileFieldGroup {
        ...FieldGroupFragment
        createdByUser {
          ...UserDataFragment
        }
      }
      filesFieldGroup {
        ...FieldGroupFragment
      }
      mostRecentPlansSet {
        id
        versionNumber
        allReviewsDoneOffline
        readyForApplicantUpdate
        fieldGroup {
          id
          fields {
            id
            file {
              id
            }
            files {
              id
            }
          }
        }
      }
    }
  }
`
export const useRecordFilesListQuery = (recordId: number) => {
  const result = useQuery<GetRecordFilesListQuery, GetRecordFilesListQueryVariables>({
    queryKey: [QueryKey.RecordFilesList, recordId],
    queryDocument: GetRecordFilesList,
    variables: { recordId },
  })
  const recordById = result.data?.recordById
  return {
    ...omit(result, 'data'),
    formFileFieldGroup: recordById?.formFileFieldGroup,
    filesFieldGroup: recordById?.filesFieldGroup,
    mostRecentPlansSet: recordById?.mostRecentPlansSet,
  }
}

const GetRecordId = gql`
  query GetRecordIdQuery($recordUuid: String!) {
    recordByUuid(uuid: $recordUuid) {
      id
    }
  }
`
export const useRecordIdQuery = (recordUuid: string) => {
  const result = useQuery<GetRecordIdQuery, GetRecordIdQueryVariables>({
    queryKey: [QueryKey.RecordId, recordUuid],
    queryDocument: GetRecordId,
    variables: {
      recordUuid,
    },
  })
  const recordId = result.data?.recordByUuid?.id

  return {
    ...omit(result, 'data'),
    recordId,
  }
}

const GetRecordUpdatedRecordTemplateHistories = gql`
  query GetRecordUpdatedRecordTemplateHistoriesQuery($recordId: Int!) {
    recordById(id: $recordId) {
      ...RecordUpdatedRecordTemplateHistoriesFragment
    }
  }
`
export const useGetRecordUpdatedRecordTemplateHistoriesFragment = (recordId: number) => {
  const { data, ...rest } = useQuery<
    GetRecordUpdatedRecordTemplateHistoriesQuery,
    GetRecordUpdatedRecordTemplateHistoriesQueryVariables
  >({
    queryKey: [QueryKey.RecordUpdatedRecordTemplateHistories, recordId],
    queryDocument: GetRecordUpdatedRecordTemplateHistories,
    variables: { recordId },
  })

  const histories = useMemo(
    () => data?.recordById?.recordTemplateUpdatedHistoryItemsDesc ?? [],
    [data?.recordById?.recordTemplateUpdatedHistoryItemsDesc]
  )

  return {
    ...rest,
    histories,
  }
}

const RenewRecordInPlace = gql`
  mutation RenewRecordInPlaceMutation($id: Int!, $input: RenewRecordInPlaceInput!) {
    renewRecordInPlace(id: $id, input: $input) {
      ...RecordOptionFragment
    }
  }
`
export const useRenewRecordInPlaceMutation = () => {
  return useMutation<RenewRecordInPlaceMutation, RenewRecordInPlaceMutationVariables>({
    mutationDocument: RenewRecordInPlace,
  })
}

// Mutation backing the renewal drawer when the assigned RenewalPolicy uses
// RenewalMethod=InPlace. Returns the same record (now flipped into a renewal
// lifecycle) so we can refresh the current record detail page.
const InitiateRenewalInPlace = gql`
  mutation InitiateRenewalInPlaceMutation($input: InitiateRenewalInPlaceInput!) {
    initiateRenewalInPlace(input: $input) {
      id
      uuid
    }
  }
`
export const useInitiateRenewalInPlaceMutation = () => {
  return useMutation<InitiateRenewalInPlaceMutation, InitiateRenewalInPlaceMutationVariables>({
    mutationDocument: InitiateRenewalInPlace,
  })
}

// Mutation backing the renewal drawer when the assigned RenewalPolicy uses
// RenewalMethod=NewRecord. Returns the newly-created child record so we can
// navigate the user to it after submission.
const InitiateRenewalNewRecord = gql`
  mutation InitiateRenewalNewRecordMutation($input: InitiateRenewalNewRecordInput!) {
    initiateRenewalNewRecord(input: $input) {
      id
      uuid
    }
  }
`
export const useInitiateRenewalNewRecordMutation = () => {
  return useMutation<InitiateRenewalNewRecordMutation, InitiateRenewalNewRecordMutationVariables>({
    mutationDocument: InitiateRenewalNewRecord,
  })
}

const GetRecordTypeId = gql`
  query GetRecordTypeIdForRecord($recordId: Int!) {
    recordById(id: $recordId) {
      id
      recordTemplate {
        id
        recordTypeId
      }
    }
  }
`
export const useGetRecordTypeIdForRecordQuery = (recordId: number) => {
  return useQuery<GetRecordTypeIdForRecord, GetRecordTypeIdForRecordVariables>({
    queryKey: [QueryKey.RecordTypeIdForRecord, recordId],
    queryDocument: GetRecordTypeId,
    variables: { recordId },
  })
}

const GetAlertsForRecord = gql`
  query GetAlertsForRecordQuery($recordId: Int!) {
    recordById(id: $recordId) {
      ...AlertsForRecordFragment
    }
  }
`

export const useGetAlertsForRecordQuery = (recordId: number) => {
  const { data, ...rest } = useQuery<GetAlertsForRecordQuery, GetAlertsForRecordQueryVariables>({
    queryKey: [QueryKey.AlertsForRecord, recordId],
    queryDocument: GetAlertsForRecord,
    variables: { recordId },
  })

  const relatedAlerts = useMemo(
    () => data?.recordById?.relatedAlerts ?? [],
    [data?.recordById?.relatedAlerts]
  )

  const alertSourceCandidates = useMemo(
    () => data?.recordById?.alertSourceCandidates ?? [],
    [data?.recordById?.alertSourceCandidates]
  )

  return {
    ...rest,
    relatedAlerts,
    alertSourceCandidates,
  }
}

const GenerateNearbyPropertiesReport = gql`
  mutation GenerateNearbyPropertiesReportMutation($input: GenerateNearbyPropertiesReportInput!) {
    generateNearbyPropertiesReport(input: $input) {
      ...GeneratedFileFragment
    }
  }
`

export const useGenerateNearbyPropertiesReportMutation = () => {
  return useMutation<
    GenerateNearbyPropertiesReportMutation,
    GenerateNearbyPropertiesReportMutationVariables
  >({
    mutationDocument: GenerateNearbyPropertiesReport,
  })
}

// not needed yet: will be used for generating record report and determining which sections to show
// ie don't show meetings checkbox if they're not enabled for the module that the record is in

// const GetRecordIndividualReport = gql`
//   query GetRecordIndividualReportQuery($recordId: Int!) {
//     recordById(id: $recordId) {
//       id
//       displayIdentifier
//       recordTemplate {
//         id
//         recordType {
//           id
//           areMeetingsEnabled
//           allowInspections
//           allowFees
//         }
//       }
//     }
//   }
// `
// export const useGetRecordIndividualReportQuery = (recordId: number) => {
//   const { data, ...rest } = useQuery<
//     GetRecordIndividualReportQuery,
//     GetRecordIndividualReportQueryVariables
//   >({
//     queryKey: [QueryKey.RecordIndividualReport, recordId],
//     queryDocument: GetRecordIndividualReport,
//     variables: { recordId },
//   })
//   const record = useMemo(() => data?.recordById, [data?.recordById])
//   return {
//     ...rest,
//     record,
//   }
// }

const GetRecordIssuedIdentifier = gql`
  query GetRecordIssuedIdentifierQuery($id: Int!) {
    recordById(id: $id) {
      ...RecordIssuedIdentifierFragment
    }
  }
`

export const useGetRecordIssuedIdentifierQuery = (recordId: number) => {
  const { data, ...rest } = useQuery<
    GetRecordIssuedIdentifierQuery,
    GetRecordIssuedIdentifierQueryVariables
  >({
    queryKey: [QueryKey.RecordIssuedIdentifier, recordId],
    queryDocument: GetRecordIssuedIdentifier,
    variables: { id: recordId },
  })
  const record = useMemo(() => data?.recordById, [data?.recordById])
  return {
    ...rest,
    record,
  }
}

const UpdateRecordIssuedIdentifier = gql`
  mutation UpdateRecordIssuedIdentifierMutation(
    $id: Int!
    $input: UpdateRecordIssuedIdentifierInput!
  ) {
    updateRecordIssuedIdentifier(id: $id, input: $input) {
      id
    }
  }
`

export const useUpdateRecordIssuedIdentifierMutation = () => {
  return useMutation<
    UpdateRecordIssuedIdentifierMutation,
    UpdateRecordIssuedIdentifierMutationVariables
  >({
    mutationDocument: UpdateRecordIssuedIdentifier,
  })
}
